import { createSelector } from 'reselect';
import { selectAuthUser } from './userSelectors';

const settingsSelector = (state) => state.settings;

const normalizeUid = (uid) => String(uid).trim().toLowerCase();

export const selectSettings = createSelector(
  settingsSelector,
  (state) => state,
);

export const selectRegistrationUrl = createSelector(
  settingsSelector,
  (state) => state.RegistrationUrl,
);

export const selectBuyCashBufferPercentage = createSelector(
  settingsSelector,
  (state) => state.BuyCashBufferPercentage,
);

export const selectMetalStreamMinimumDepositAmount = createSelector(
  settingsSelector,
  (state) => state.MetaltreamMinimumDepositAmount,
);

export const selectMetalStreamMaximumDepositAmount = createSelector(
  settingsSelector,
  (state) => state.MetalStreamMaximumDepositAmount,
);

export const selectNumberOfAllowedAchDepositAccounts = createSelector(
  settingsSelector,
  (state) => state.NumberOfAllowedAchDepositAccounts,
);

export const selectAchDepositLimit = createSelector(
  settingsSelector,
  (state) => state.AchDepositLimit,
);

export const selectCheckDepositLimit = createSelector(
  settingsSelector,
  (state) => state.CheckDepositLimit,
);

export const selectSettlementDaysInfo = createSelector(
  settingsSelector,
  (state) => state.AchSettlementDaysInfo,
);

export const selectWireWithdrawalFee = createSelector(
  settingsSelector,
  (state) => state.WireWithdrawalFee,
);

export const selectIsReCaptchaEnabled = createSelector(
  settingsSelector,
  (state) => state.EnableGoogleReCaptcha,
);

export const selectIsTwoFAEnabled = createSelector(
  settingsSelector,
  (state) => state.IsBroker2FAActive,
);

export const selectPortalGatewayISOCodesListToIgnoreAreaCode = createSelector(
  settingsSelector,
  (state) => state.PortalGatewayISOCodesListToIgnoreAreaCode,
);

export const selectSupportEmail = createSelector(
  settingsSelector,
  (state) => state.SupportEmail,
);

export const selectAddUpdateEmailMailingAddressUrl = createSelector(
  settingsSelector,
  (state) => state.AddUpdateEmailMailingAddressUrl,
);

export const selectSegregatedStorageEnabled = createSelector(
  settingsSelector,
  (state) => state?.SegregatedStorageEnabled,
);

export const selectIsAddAddressesEnabled = createSelector(
  settingsSelector,
  (state) => state?.SelfServeAddressAllowed,
);

export const selectMailInEnabled = createSelector(
  settingsSelector,
  (state) => state?.MailInStorageEnabled,
);

export const selectMailInLearnMoreLink = createSelector(
  settingsSelector,
  (state) => state?.MailInLearnMoreUrl,
);

export const selectSellMailInStorageUrl = createSelector(
  settingsSelector,
  (state) => state?.SellMailInStorageUrl,
);

// [Insider+] Feature flag — gates the Insider+ routes and header entries.
export const selectIsInsiderPlusActive = createSelector(
  settingsSelector,
  (state) => state?.IsInsiderPlusActive,
);

// [Insider+] Phase-1 early-access whitelist of UserUids. Lets specific users
// in before their broker is switched on (IsInsiderPlusActive = false).
export const selectInsiderPlusEnabledUserUids = createSelector(
  settingsSelector,
  (state) => state?.InsiderPlusEnabledUserUids,
);

// [Insider+] Per-user gate (OR, not AND — the whitelist is an override):
//   Phase 1: while IsInsiderPlusActive is false, only UserUids explicitly
//            listed in InsiderPlusEnabledUserUids get early access.
//   Phase 2: once IsInsiderPlusActive is true, everyone is enabled and the
//            whitelist becomes a no-op.
// An empty/missing whitelist grants no early access (falls back to the flag).
// UID comparison is case-insensitive.
export const selectIsInsiderPlusEnabledForUser = createSelector(
  selectIsInsiderPlusActive,
  selectInsiderPlusEnabledUserUids,
  selectAuthUser,
  (isActive, enabledUserUids, user) => {
    const isWhitelisted =
      Array.isArray(enabledUserUids) &&
      !!user?.UserUid &&
      enabledUserUids
        .filter((uid) => uid != null)
        .some((uid) => normalizeUid(uid) === normalizeUid(user.UserUid));

    const isEnabled = !!isActive || isWhitelisted;

    return isEnabled;
  },
);

export const selectIsSettingsLoaded = createSelector(
  settingsSelector,
  (state) => Boolean(state?.BrokerCode),
);
